<template>
  <div>
    <!-- 自分のデータプロファイル -->
    <data-profile-info
        v-if="tradeContract.selfDataProfileId && !hideSelfDataProfile"
        class="mb-5"
        :data-profile-view="selfDataProfile"
        :loading="selfDataProfileLoading">
      <template #title>
        <slot name="self-data-profile-title">データプロファイル</slot>
      </template>
      <template #pull-request-parameter>
        <v-col cols="3">
          <text-field-like
              label="PUSH/PULL"
              :value="tradeContract.sendType === 'pull' ? 'PULL リクエスト可' : 'PULL リクエスト不可'"
              hide-details>
          </text-field-like>
        </v-col>
        <v-col cols="3">
          <text-field-like
              v-if="tradeContract.sendType === 'pull'"
              label="PULL リクエストパラメータの指定方法"
              :value="parameterDescription"
              :loading="parameterDescriptionLoading"
              placeholder="未設定"
              hide-details
              multi-line>
          </text-field-like>
        </v-col>
      </template>
      <template #append>
        <v-divider class="mt-3"></v-divider>
        <data-property-profile-list
            :data-property-profiles="selfDataPropertyProfiles"
            :loading="selfDataPropertyProfilesLoading">
          <template #tail>
            <el-table-column
                v-if="tradeContract.otherDataProfileId"
                label="取引先データとの対応"
                align="center"
                width="200">
              <template #default="{row}">
                <table-inline>
                  <text-field-like
                      :value="getMappedDataPropertyProfileBySelf(row.id).name">
                    <template #append>
                      <connector-error-indicator :connector="row.connector"></connector-error-indicator>
                    </template>
                  </text-field-like>
                </table-inline>
              </template>
            </el-table-column>
          </template>
        </data-property-profile-list>
      </template>
    </data-profile-info>
    <v-alert
        v-if="!tradeContract.selfDataProfileId && !hideSelfDataProfile"
        class="mb5"
        icon="mdi-information-outline"
        outlined>
      <slot name="self-data-profile-title">データプロファイル</slot>を選択していません
    </v-alert>

    <slot v-bind:tradeContract="tradeContract" name="after-self-data-profile"></slot>

    <!-- 自分のサービスプロファイル -->
    <service-profile-info
        v-if="tradeContract.selfServiceProfileId && !hideSelfServiceProfile"
        class="mb-5"
        :service-profile-view="selfServiceProfile"
        :loading="selfServiceProfileLoading">
      <template #title>
        <slot name="self-service-profile-title">サービスプロファイル</slot>
      </template>
      <template #append>
        <v-card class="pa-3 mt-3 elevation-1" outlined>
          <v-row dense>
            <v-col cols="3">
              <text-field-like
                  label="プロセスプロファイル名"
                  :value="selfProcessProfile.localProcessComponentModel && selfProcessProfile.localProcessComponentModel.name"
                  :value-id="selfProcessProfile.id"
                  :placeholder="selfProcessProfileLoading ? '' : '\u2E3B'">
              </text-field-like>
            </v-col>
            <v-col cols="3">
              <text-field-like
                  label="プロセス実装ID"
                  :value="selfProcessProfile.processImplementation && selfProcessProfile.processImplementation.id"
                  :placeholder="selfProcessProfileLoading ? '' : '\u2E3B'">
              </text-field-like>
            </v-col>
          </v-row>
        </v-card>
        <v-divider class="mt-3"></v-divider>
        <el-table
            :data="selfEventProfiles"
            :class="{'hide-empty-text': selfEventProfilesLoading}"
            border>
          <el-table-column
              label="イベント名"
              width="200"
              prop="name">
            <template #default="{row}">
              <table-inline>
                <text-field-like
                    :value="row.name"
                    :value-local-id="row.localId"
                    placeholder="未設定">
                  <template #prepend>
                    <el-tooltip v-if="row.eventProfileableType === 'EventImplementation' && tradeContract.isSelfProduce"
                                content="トリガとなるイベント" :open-delay="500" placement="left">
                      <v-icon>mdi-lightning-bolt</v-icon>
                    </el-tooltip>
                    <el-tooltip v-else-if="row.eventProfileableType === 'EventImplementation' && tradeContract.isSelfConsume"
                                content="イベント" :open-delay="500" placement="left">
                      <v-icon>mdi-lightning-bolt</v-icon>
                    </el-tooltip>
                    <el-tooltip v-else-if="row.eventProfileableType === 'CalendarImplementation'"
                                content="カレンダ" :open-delay="500" placement="left">
                      <v-icon>mdi-calendar</v-icon>
                    </el-tooltip>
                  </template>
                </text-field-like>
              </table-inline>
            </template>
          </el-table-column>
          <el-table-column
              label="説明"
              prop="description">
            <template #default="{row}">
              <table-inline>
                <text-field-like
                    :value="row.description"
                    placeholder="未設定"
                    multi-line>
                </text-field-like>
              </table-inline>
            </template>
          </el-table-column>
          <el-table-column
              label="プロセス名"
              width="200"
              prop="processComponentModel.name">
            <template #default="{row}">
              <table-inline>
                <text-field-like
                    :value="row.processComponentModel && row.processComponentModel.name"
                    :placeholder="row.processComponentModel ? '未設定' : '\u2E3B'"
                    multi-line>
                </text-field-like>
              </table-inline>
            </template>
          </el-table-column>
          <template v-if="tradeContract.isSelfProduce">
            <el-table-column
                label="説明"
                prop="processComponentModel.description">
              <template #default="{row}">
                <table-inline>
                  <text-field-like
                      :value="row.processComponentModel && row.processComponentModel.description"
                      :placeholder="row.processComponentModel ? '未設定' : '\u2E3B'"
                      multi-line>
                  </text-field-like>
                </table-inline>
              </template>
            </el-table-column>
          </template>
          <template v-if="tradeContract.isSelfConsume">
            <el-table-column
                label="記録"
                prop="is_monitored"
                width="50"
                align="center">
              <template #default="{row}">
                <table-inline>
                  <readonly-checkbox :value="row.isMonitored"></readonly-checkbox>
                </table-inline>
              </template>
            </el-table-column>
            <el-table-column
                label="記録内容"
                prop="monitoringContent">
              <template #default="{row}">
                <table-inline>
                  <text-field-like
                      :value="row.isMonitored ? row.monitoringContent : null"
                      :placeholder="row.isMonitored ? '未設定' : '\u2E3B'"
                      multi-line>
                  </text-field-like>
                </table-inline>
              </template>
            </el-table-column>
            <el-table-column
                v-for="pot in processOperationTypes"
                :key="pot.name"
                :label="pot.label"
                :prop="pot.prop"
                width="50"
                align="center">
              <template #default="{row}">
                <table-inline>
                  <readonly-checkbox :value="row[pot.prop]"></readonly-checkbox>
                </table-inline>
              </template>
            </el-table-column>
          </template>
          <template #append>
            <v-row
                justify="center"
                class="pa-10"
                v-if="selfEventProfilesLoading">
              <v-progress-circular indeterminate/>
            </v-row>
          </template>
        </el-table>
      </template>
    </service-profile-info>
    <v-alert
        v-if="!tradeContract.selfServiceProfileId && !hideSelfServiceProfile"
        class="mb5"
        icon="mdi-information-outline"
        outlined>
      <slot name="self-service-profile-title">サービスプロファイル</slot>を選択していません
    </v-alert>

    <slot v-bind:tradeContract="tradeContract" name="after-self-service-profile"></slot>

    <!-- 相手のデータプロファイル -->
    <data-profile-info
        v-if="tradeContract.otherDataProfileId && !hideOtherDataProfile"
        class="mb-5"
        :data-profile-view="otherDataProfile"
        :loading="otherDataProfileLoading">
      <template #title>
        <slot name="other-data-profile-title">取引先データプロファイル</slot>
      </template>
      <template #append>
        <v-divider class="mt-3"></v-divider>
        <data-property-profile-list
            :data-property-profiles="otherDataPropertyProfiles"
            :loading="otherDataPropertyProfilesLoading">
        </data-property-profile-list>
      </template>
    </data-profile-info>

    <slot v-bind:tradeContract="tradeContract" name="after-other-data-profile"></slot>

    <!-- 相手のサービスプロファイル -->
    <service-profile-info
        v-if="tradeContract.otherServiceProfileId && !hideOtherServiceProfile"
        class="mb-5"
        :service-profile-view="otherServiceProfile"
        :loading="otherServiceProfileLoading">
      <template #title>
        <slot name="other-service-profile-title">取引先サービスプロファイル</slot>
      </template>
      <template #subtitle>
        (取引先の対応により変更される可能性があります)
      </template>
      <template #append>
        <v-card class="pa-3 mt-3 elevation-1" outlined>
          <v-row dense>
            <v-col cols="3">
              <text-field-like
                  label="プロセスプロファイル名"
                  :value="otherProcessProfile.processComponentModel && otherProcessProfile.processComponentModel.name"
                  :value-id="otherProcessProfile.id"
                  :placeholder="otherProcessProfileLoading ? '' : '\u2E3B'">
              </text-field-like>
            </v-col>
          </v-row>
        </v-card>
        <v-divider
          v-if="!hideOtherEventProfile"
          class="mt-3"
        />
        <el-table
            v-if="!hideOtherEventProfile"
            :data="otherEventProfiles"
            :class="{'hide-empty-text': otherEventProfilesLoading}"
            border>
          <el-table-column
              label="イベント名"
              width="200"
              prop="name">
            <template #default="{row}">
              <table-inline>
                <text-field-like
                    :value="row.name"
                    :value-local-id="row.localId"
                    placeholder="未設定">
                  <template #prepend>
                    <el-tooltip v-if="row.eventProfileableType === 'EventImplementation' && tradeContract.isOtherProduce"
                                content="トリガとなるイベント" :open-delay="500" placement="left">
                      <v-icon>mdi-lightning-bolt</v-icon>
                    </el-tooltip>
                    <el-tooltip v-else-if="row.eventProfileableType === 'EventImplementation' && tradeContract.isOtherConsume"
                                content="イベント" :open-delay="500" placement="left">
                      <v-icon>mdi-lightning-bolt</v-icon>
                    </el-tooltip>
                    <el-tooltip v-else-if="row.eventProfileableType === 'CalendarImplementation'"
                                content="カレンダ" :open-delay="500" placement="left">
                      <v-icon>mdi-calendar</v-icon>
                    </el-tooltip>
                  </template>
                </text-field-like>
              </table-inline>
            </template>
          </el-table-column>
          <el-table-column
              label="説明"
              prop="description">
            <template #default="{row}">
              <table-inline>
                <text-field-like
                    :value="row.description"
                    placeholder="未設定"
                    multi-line>
                </text-field-like>
              </table-inline>
            </template>
          </el-table-column>
          <el-table-column
              label="プロセス名"
              width="200"
              prop="processComponentModel.name">
          </el-table-column>
          <template v-if="tradeContract.isOtherProduce">
            <el-table-column
                label="説明"
                prop="processComponentModel.description">
              <template #default="{row}">
                <table-inline>
                  <text-field-like
                      :value="row.processComponentModel && row.processComponentModel.description"
                      placeholder="未設定"
                      multi-line>
                  </text-field-like>
                </table-inline>
              </template>
            </el-table-column>
          </template>
          <template v-if="tradeContract.isOtherConsume">
            <el-table-column
                label="記録"
                prop="is_monitored"
                width="80"
                align="center">
              <template #header="{column}">
                <span>{{ column.label }}</span>
                <el-tooltip placement="top" :open-delay="500">
                  <template #content>
                    <div style="font-size:11pt; width:520px; line-height: 1.5;">
                      チェックの入っているイベント内容が、取引先によって記録されています。<br>
                      取引先によって記録された内容は、本取引の契約事項に従い、連携マネージャから確認することができます。
                    </div>
                  </template>
                  <template #default>
                    <v-icon
                        style="margin-left: 5px;margin-bottom: 5px;"
                        color="primary"
                        small>
                      mdi-help-circle
                    </v-icon>
                  </template>
                </el-tooltip>
              </template>
              <template #default="{row}">
                <table-inline>
                  <readonly-checkbox :value="row.isMonitored"></readonly-checkbox>
                </table-inline>
              </template>
            </el-table-column>
            <el-table-column
                label="記録内容"
                prop="monitoringContent">
              <template #default="{row}">
                <table-inline>
                  <text-field-like
                      :value="row.isMonitored ? row.monitoringContent : null"
                      :placeholder="row.isMonitored ? '未設定' : '\u2E3B'"
                      multi-line>
                  </text-field-like>
                </table-inline>
              </template>
            </el-table-column>
            <el-table-column
                v-for="pot in processOperationTypes"
                :key="pot.name"
                :label="pot.label"
                :prop="pot.prop"
                width="50"
                align="center">
              <template #default="{row}">
                <table-inline>
                  <readonly-checkbox :value="row[pot.prop]"></readonly-checkbox>
                </table-inline>
              </template>
            </el-table-column>
          </template>
          <template #append>
            <v-row
                justify="center"
                class="pa-10"
                v-if="otherEventProfilesLoading">
              <v-progress-circular indeterminate/>
            </v-row>
          </template>
        </el-table>
      </template>
    </service-profile-info>

    <slot v-bind:tradeContract="tradeContract" name="after-other-service-profile"></slot>

    <!-- 契約プロファイル -->
    <v-card outlined elevation="0" class="contract-profile">
      <v-card-title>契約内容</v-card-title>
      <v-card-text>
<!--        <v-row dense v-if="contractProfileTemplate">-->
<!--          <v-col>-->
<!--            <text-field-like-->
<!--                label="契約プロファイルテンプレート名"-->
<!--                :value="contractProfileTemplate.name"-->
<!--                hide-details>-->
<!--            </text-field-like>-->
<!--          </v-col>-->
<!--        </v-row>-->

        <v-card outlined>
          <v-subheader>契約文書</v-subheader>
          <document-contract-profile-tab
              :trade-contract-id="isTradeContractView ? tradeContract.id : undefined"
              :trade-contract-format-id="isTradeContractFormatView ? tradeContract.id : undefined"
              :contract-profiles="contractProfiles"
              :loading="contractProfilesLoading"
              readonly
              hide-selection>
          </document-contract-profile-tab>
        </v-card>

        <v-card outlined class="mt-3">
          <v-subheader>契約条項</v-subheader>
          <generic-contract-profile-tab
              :trade-contract-id="isTradeContractView ? tradeContract.id : undefined"
              :trade-contract-format-id="isTradeContractFormatView ? tradeContract.id : undefined"
              :contract-profiles="contractProfiles"
              notation-type="clause"
              :loading="contractProfilesLoading"
              readonly
              hide-selection>
          </generic-contract-profile-tab>
        </v-card>

        <v-card outlined class="mt-3">
          <v-subheader>契約事項</v-subheader>
          <generic-contract-profile-tab
              :trade-contract-id="isTradeContractView ? tradeContract.id : undefined"
              :trade-contract-format-id="isTradeContractFormatView ? tradeContract.id : undefined"
              :contract-profiles="contractProfiles"
              notation-type="parameter"
              :loading="contractProfilesLoading"
              readonly
              hide-selection>
          </generic-contract-profile-tab>
        </v-card>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import TradeContractView, {emptyTradeContractView} from "@/components/pages/contract/TradeContract/TradeContractView";
import TradeContractInfo from "@/components/organisms/TradeContractInfo.vue";
import DataProfileSource from "@/components/organisms/DataProfileSource";
import DataPropertyProfileSource from "@/components/organisms/DataPropertyProfileSource";
import DataProfileInfo from "@/components/organisms/DataProfileInfo.vue";
import DataPropertyProfileList from "@/components/organisms/DataPropertyProfileList.vue";
import ServiceProfileSource from "@/components/organisms/ServiceProfileSource";
import ServiceProfileInfo from "@/components/organisms/ServiceProfileInfo.vue";
import DocumentContractProfileTab from "@/components/organisms/ContractProfileTemplatePanel/DocumentContractProfileTab.vue";
import GenericContractProfileTab from "@/components/organisms/ContractProfileTemplatePanel/GenericContractProfileTab.vue";
import {mapActions, mapGetters} from "vuex";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import Formatter from "@/utils/Formatter";
import Qs from "qs";
import TableInline from "@/components/atoms/TableInline.vue";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import ProcessProfileSource from "@/components/organisms/ProcessProfileSource";
import {capitalize} from "@/utils/StringUtil";
import {PROCESS_OPERATION_OPERATION_TYPES} from "@/constants";
import DataPropertyProfileLinkManager
  from "@/components/pages/contract/TradeContract/AdaptContract/DataPropertyProfileLinkManager";
import DataPropertyProfileConnector
  from "@/components/pages/contract/TradeContract/AdaptContract/DataPropertyProfileConnector";
import ConnectorErrorIndicator from "@/components/pages/contract/TradeContract/AdaptContract/ConnectorErrorIndicator.vue";
import MessageHistory from "@/components/pages/contract/TradeContract/MessageHistory.vue";
import {DateTime} from "luxon";
import {isNullish} from "@/utils/TypeUtil";
import TradeContractBaseView from "@/components/pages/contract/TradeContract/TradeContractBaseView";
import TradeContractViewPanel from "@/components/pages/contract/TradeContract/TradeContractViewPanel.vue";
import TradeContractFormatView from "@/components/pages/contract/TradeContractFormat/TradeContractFormatView";
import ContractProfileTemplatePanel from "@/components/organisms/ContractProfileTemplatePanel.vue";

export default {
  name: "TradeContractViewSolePanel",
  components: {
    ConnectorErrorIndicator,
    ReadonlyCheckbox,
    TableInline,
    TextFieldLike,
    GenericContractProfileTab,
    DocumentContractProfileTab,
    ServiceProfileInfo, DataPropertyProfileList, DataProfileInfo},
  mixins: [DataProfileSource, DataPropertyProfileSource, ServiceProfileSource, ProcessProfileSource, Formatter],
  props: {
    tradeContract: {
      type: TradeContractBaseView /* as PropType<TradeContractBaseView[]> */,
      required: true
    },
    activate: {
      type: Boolean,
      required: true
    },
    hideSelfDataProfile: {
      type: Boolean,
      default: false
    },
    hideSelfServiceProfile: {
      type: Boolean,
      default: false
    },
    hideOtherDataProfile: {
      type: Boolean,
      default: false
    },
    hideOtherServiceProfile: {
      type: Boolean,
      default: false
    },
    hideOtherEventProfile: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      selfDataProfile: {},
      selfDataProfileLoading: true,
      selfDataPropertyProfiles: [],
      selfDataPropertyProfilesLoading: true,
      selfServiceProfile: {},
      selfServiceProfileLoading: true,
      selfProcessProfile: {},
      selfProcessProfileLoading: true,
      selfEventProfiles: [],
      selfEventProfilesLoading: true,
      otherDataProfile: {},
      otherDataProfileLoading: true,
      otherDataPropertyProfiles: [],
      otherDataPropertyProfilesLoading: true,
      otherServiceProfile: {},
      otherServiceProfileLoading: true,
      otherProcessProfile: {},
      otherProcessProfileLoading: true,
      otherEventProfiles: [],
      otherEventProfilesLoading: true,
      // contractProfileTemplate: null,
      contractProfiles: [],
      contractProfilesLoading: true,
      processOperationTypes: ['read', 'update', 'delete'].map(t => ({
        name: t,
        prop: 'can' + capitalize(t),
        label: PROCESS_OPERATION_OPERATION_TYPES[t],
      })),
      linkManager: new DataPropertyProfileLinkManager(() => this.tradeContract),
      now: DateTime.now(),
    };
  },
  computed: {
    ...mapGetters({
      hcsClient: 'hcs/client',
    }),
    isTradeContractFormatView() {
        return this.tradeContract instanceof TradeContractFormatView;
    },
    isTradeContractView() {
        return this.tradeContract instanceof TradeContractView;
    },
    parameterDescription() {
      if(this.tradeContract.isSelfProduce) {
        return this.selfDataProfile?.dataImplementation?.parameterDescription;
      } else {
        return this.otherDataProfile?.dataImplementation?.parameterDescription;
      }
    },
    parameterDescriptionLoading() {
      if(this.tradeContract.isSelfProduce) {
        return this.selfDataProfileLoading;
      } else {
        return this.otherDataProfileLoading;
      }
    },
  },
  methods: {
    ...mapActions({
      gqlQuery: 'hds/gqlQuery',
    }),
    isEmptyObject(obj) {
      return Object.keys(obj).length === 0;
    },
    async load() {
      // 自分のデータプロファイル
      const selfDataProfileId = this.tradeContract.selfDataProfileId;
      if(selfDataProfileId && !this.hideSelfDataProfile) {
        this.selfDataProfile = await this.loadDataProfile(selfDataProfileId);
        this.selfDataProfileLoading = false;
        this.selfDataPropertyProfiles = (await this.loadDataPropertyProfiles(selfDataProfileId))
            .map(dpp => {
              const dpi = dpp.dataPropertyImplementation;
              // const commonDpd = dpp.commonDataPropertyDefinition;
              const localDpd = dpp.localDataPropertyDefinition;
              return {
                ...dpp,
                index: dpi.index,
                defaultValue: dpi.defaultValue,
                name: localDpd?.name,
                description: localDpd?.description,
                dataType: localDpd?.dataType ?? 'string',
                isPrimaryKey: localDpd?.isPrimaryKey ?? false,
                isRequired: localDpd?.isRequired ?? false,
              };
            })
            .map(dpp => ({
              ...dpp,
              connector: new DataPropertyProfileConnector({
                serviceType: this.tradeContract.serviceType,
                dataPropertyProfileId: () => dpp.id,
                defaultValue: () => dpp.defaultValue,
                isRequired: () => dpp.isRequired,
                dataType: () => dpp.dataType,
              }).register(this.linkManager)
            }));
        this.selfDataPropertyProfilesLoading = false;
      } else {
        this.selfDataProfileLoading = false;
        this.selfDataPropertyProfilesLoading = false;
      }

      // 自分のサービスプロファイル
      const selfServiceProfileId = this.tradeContract.selfServiceProfileId;
      if(selfServiceProfileId && !this.hideSelfServiceProfile) {
        this.selfServiceProfile = await this.loadServiceProfile(selfServiceProfileId);
      }
      this.selfServiceProfileLoading = false;

      // 自分のプロセスプロファイル
      if(!this.hideSelfServiceProfile && this.tradeContract.selfProcessProfileId) {
        this.selfProcessProfile = (await this.loadProcessProfile(this.tradeContract.selfProcessProfileId)) ?? {};
      }
      this.selfProcessProfileLoading = false;

      // 自分のイベントプロファイル
      if(!this.hideSelfServiceProfile) {
        this.selfEventProfiles = await this.loadEventProfiles(this.tradeContract.selfEventProfileIds, this.tradeContract.serviceType);
      }
      this.selfEventProfilesLoading = false;

      // 相手のデータプロファイル
      const otherDataProfileId = this.tradeContract.otherDataProfileId;
      if(otherDataProfileId /*&& (!this.hideOtherDataProfile || this.tradeContract.isSelfConsume)*/) {
        this.otherDataProfile = await this.loadAssetsDataProfile(otherDataProfileId);
        this.otherDataProfileLoading = false;
        this.otherDataPropertyProfiles = (await this.loadAssetsDataPropertyProfiles(otherDataProfileId))
            .map(dpp => {
              const dpi = dpp.dataPropertyImplementation;
              const commonDpd = dpp.commonDataPropertyDefinition;
              const localDpd = dpp.localDataPropertyDefinition;
              const dpd = commonDpd ?? localDpd
              return {
                ...dpp,
                index: dpi.index,
                defaultValue: dpi.defaultValue,
                name: commonDpd?.name,
                nameAlt: localDpd?.name,
                description: dpd?.description,
                dataType: dpd?.dataType ?? 'string',
                isPrimaryKey: dpd?.isPrimaryKey ?? false,
                isRequired: dpd?.isRequired ?? false,
              };
            })
            .map(dpp => ({
              ...dpp,
              connector: new DataPropertyProfileConnector({
                serviceType: this.tradeContract.otherServiceType,
                dataPropertyProfileId: () => dpp.id,
                defaultValue: () => dpp.defaultValue,
                isRequired: () => dpp.isRequired,
                dataType: () => dpp.dataType,
              }).register(this.linkManager)
            }));
        this.otherDataPropertyProfilesLoading = false;
      } else {
        this.otherDataProfileLoading = false;
        this.otherDataPropertyProfilesLoading = false;
      }

      // データ項目プロファイルマッピング
      this.linkManager.dataPropertyProfileMaps = this.tradeContract.dataPropertyProfileMaps;

      // 相手のサービスプロファイル
      const otherServiceProfileId = this.tradeContract.otherServiceProfileId;
      if(otherServiceProfileId && !this.hideOtherServiceProfile) {
        this.otherServiceProfile = await this.loadAssetsServiceProfile(otherServiceProfileId);
      }
      this.otherServiceProfileLoading = false;

      // 相手のプロセスプロファイル
      const otherProcessProfileId = this.tradeContract.otherProcessProfileId;
      if(otherProcessProfileId && !this.hideOtherServiceProfile) {
        this.otherProcessProfile = (await this.loadAssetsProcessProfile(otherProcessProfileId)) ?? {};
      }
      this.otherProcessProfileLoading = false;

      // 相手のイベントプロファイル
      if(!this.hideOtherServiceProfile && !this.hideOtherEventProfile) {
        this.otherEventProfiles = await this.loadEventProfiles(this.tradeContract.otherEventProfileIds, this.tradeContract.otherServiceType);
      }
      this.otherEventProfilesLoading = false;

      // // 契約プロファイルテンプレート
      // if(this.tradeContract.amiProposer) {
      //   const {data: contractProfileTemplate} = await this.hcsClient.get(`/contract_profile_templates/${this.tradeContract.contractProfileTemplateId}`);
      //   this.contractProfileTemplate = contractProfileTemplate;
      // }

      // 契約プロファイル
      const contractProfiles = await this.tradeContract.fetchContractProfiles();
      this.contractProfiles = contractProfiles.filter(cp => cp.selected);
      this.contractProfilesLoading = false;
    },
    async loadEventProfiles(eventProfileIds, serviceType) {
      if(!eventProfileIds || eventProfileIds.length === 0) {
        return [];
      }

      const axiosGetConfig = {
        params: {
          q: {idIn: eventProfileIds}
        },
        paramsSerializer: function (params) {
          return Qs.stringify(params, { arrayFormat: 'brackets', skipNulls: true })
        }
      };

      let result;
      if (serviceType === 'produce') {
        ({data: result} = await this.hcsClient.get('/assets/producer_event_profiles', axiosGetConfig));
      } else if (serviceType === 'consume') {
        ({data: result} = await this.hcsClient.get('/assets/consumer_event_profiles', axiosGetConfig));
      } else {
        throw new Error(`Invalid service type: ${serviceType}`);
      }

      return result;
    },
    getMappedDataPropertyProfileBySelf(selfDataPropertyProfileId, defaultValue = {}) {
      const links = this.linkManager.get({selfDataPropertyProfileId});
      if(links.length === 0) {
        return defaultValue;
      }
      return this.otherDataPropertyProfiles.find(dpp => dpp.id === links[0].otherDataPropertyProfileId) ?? defaultValue;
    },
  },
  watch: {
    activate: {
      immediate: true,
      handler(newActivate, oldActivate) {
        if(newActivate && !oldActivate) {
          this.load();
        } else if(!newActivate && oldActivate) {
          for(const [k, v] of Object.entries(this.$options.data.apply(this))) {
            this.$set(this, k, v);
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "src/theme";

.contract-profile ::v-deep {
  .secondary-toolbar {
    display: none;
  }

  .v-subheader {
    background-color: $primaryKeyColor;
    color: white;
  }
}

.v-alert {
  border-color: rgba(0,0,0,0.12) !important;

  ::v-deep .v-alert__content {
    text-align: left;
  }
}

</style>
